/** Labels the zone of the existing renderer; it never converts its clock. */
export function timeZoneLabel(date: Date, timeZone?: string): string {
  if (Number.isNaN(date.valueOf())) return "Time zone unavailable";
  const zone = timeZone ?? new Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (["UTC", "Etc/UTC", "Etc/GMT", "GMT"].includes(zone)) return "UTC";
  if (["Asia/Kolkata", "Asia/Calcutta"].includes(zone)) return "IST";
  if (zone === "Asia/Bangkok") return "ICT";
  const name = new Intl.DateTimeFormat("en-GB", { timeZone: zone, timeZoneName: "short" })
    .formatToParts(date).find((part) => part.type === "timeZoneName")?.value;
  if (name === "GMT" || name === "UTC") return "UTC";
  const offset = name?.match(/^(?:GMT|UTC)([+-])(\d{1,2})(?::(\d{2}))?$/);
  if (offset) return `UTC${offset[1]}${offset[2].padStart(2, "0")}:${offset[3] ?? "00"}`;
  return name || zone || "Time zone unavailable";
}

export function formatLocalTimestamp(date: Date, options?: Intl.DateTimeFormatOptions): string {
  if (Number.isNaN(date.valueOf())) return date.toLocaleString(undefined, options);
  return `${date.toLocaleString(undefined, options)} ${timeZoneLabel(date, options?.timeZone)}`;
}

/** A date projection of an event instant, not a calendar/business date. */
export function formatLocalEventDate(date: Date): string {
  if (Number.isNaN(date.valueOf())) return date.toLocaleDateString();
  return `${date.toLocaleDateString()} ${timeZoneLabel(date)}`;
}

/** Raw ISO displays retain their recorded wall clock and offset, with no conversion. */
export function formatRecordedTimestamp(value: string, dateOnly = false): string {
  if (!/[T ]\d{2}:\d{2}/.test(value) || Number.isNaN(new Date(value).valueOf())) return value;
  const offset = value.match(/(Z|[+-]\d{2}:?\d{2})$/i)?.[1];
  const zone = !offset ? "Time zone not recorded"
    : /^(Z|[+-]00:?00)$/i.test(offset) ? "UTC"
      : `UTC${offset.slice(0, 3)}:${offset.slice(-2)}`;
  return `${dateOnly ? value.slice(0, 10) : value} ${zone}`;
}
